/**
 * A tiny tween engine driven by an explicit clock.
 *
 * `updateTweens(now)` is called once per animation frame from the render
 * loop, which means tests can step time by hand and every animation in the
 * app shares one timebase with the 3D scene.
 */

import { type EaseFn, ease } from './ease.ts';

export interface TweenOptions {
  from?: number;
  to?: number;
  duration?: number;
  delay?: number;
  ease?: EaseFn;
  onUpdate?: (value: number, t: number, eased: number) => void;
  onComplete?: () => void;
}

/** A tween's promise resolves `true` when it finished, `false` when cancelled. */
export interface Tween extends Promise<boolean> {
  cancel: (finish?: boolean) => void;
}

interface TweenState {
  t0: number;
  done: boolean;
  options: Required<Omit<TweenOptions, 'onUpdate' | 'onComplete'>> &
    Pick<TweenOptions, 'onUpdate' | 'onComplete'>;
  resolve: (finished: boolean) => void;
}

const running = new Set<TweenState>();

const DEFAULTS = {
  from: 0,
  to: 1,
  duration: 500,
  delay: 0,
  ease: ease.inOutCubic,
} as const;

export function tween(options: TweenOptions = {}): Tween {
  const o = { ...DEFAULTS, ...options };
  let resolve!: (finished: boolean) => void;
  const promise = new Promise<boolean>((res) => {
    resolve = res;
  }) as Tween;

  const state: TweenState = {
    t0: performance.now() + o.delay,
    done: false,
    options: o,
    resolve,
  };
  running.add(state);

  promise.cancel = (finish = false) => {
    if (state.done) return;
    state.done = true;
    running.delete(state);
    if (finish) {
      o.onUpdate?.(o.to, 1, 1);
      o.onComplete?.();
    }
    resolve(finish);
  };
  return promise;
}

/** Advance every live tween to `now` (milliseconds, same clock as `tween`). */
export function updateTweens(now: number): void {
  for (const s of running) {
    if (s.done) {
      running.delete(s);
      continue;
    }
    const o = s.options;
    const raw = (now - s.t0) / o.duration;
    if (raw < 0) continue;
    const t = raw >= 1 ? 1 : raw;
    const eased = o.ease(t);
    o.onUpdate?.(o.from + (o.to - o.from) * eased, t, eased);
    if (t >= 1) {
      s.done = true;
      running.delete(s);
      o.onComplete?.();
      s.resolve(true);
    }
  }
}

export const wait = (ms: number): Promise<void> =>
  new Promise((r) => {
    setTimeout(r, ms);
  });

export class CancelledError extends Error {
  constructor() {
    super('cancelled');
    this.name = 'CancelledError';
  }
}

/**
 * A cancellable sequence token. The record-swap choreography is a chain of
 * awaited tweens; a token lets a newly requested swap abandon the old one
 * without leaving half-animated meshes behind.
 */
export class Token {
  cancelled = false;
  #tracked: Tween[] = [];

  cancel(): void {
    this.cancelled = true;
    for (const p of this.#tracked) p.cancel(false);
    this.#tracked.length = 0;
  }

  track(p: Tween): Tween {
    if (this.cancelled) {
      p.cancel(false);
      return p;
    }
    this.#tracked.push(p);
    return p;
  }

  check(): void {
    if (this.cancelled) throw new CancelledError();
  }
}
